import classNames from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { DotButton } from "@talladega/components";
import { GetBackgroundStyle, IsNodeStructure } from "@talladega/helpers";
import {
  ActionButton,
  CmsButtonType,
  GalleryAndContentLayout,
} from "@talladega/types";
import { EmbeddedVideoPlayer } from "../EmbeddedVideoPlayer";
import { CmsHeading } from "../CmsHeading";

const MAX_PIXEL_WIDTH = 10000;
const MAX_PIXEL_HEIGHT = 10000;

type Props = {
  layout: GalleryAndContentLayout;
  cmsButton: (
    setButtonModalState: React.Dispatch<
      React.SetStateAction<{ show: boolean; actionType: ActionButton }>
    >,
    button: CmsButtonType,
    htmlIdRef?: React.MutableRefObject<HTMLDivElement | null>
  ) => ReactNode;
  cloudflareImage: (options: {
    src?: string;
    alt?: string;
    fill?: boolean;
    width?: number;
    height?: number;
    className?: string;
  }) => ReactNode;

  cmsButtonModalContainer: (buttonModalState: {
    show: boolean;
    actionType: ActionButton;
  }) => ReactNode;

  baseDomain: string;
};

export const GalleryAndContent = ({
  layout,
  cmsButton,
  cloudflareImage,
  cmsButtonModalContainer,
  baseDomain,
}: Props) => {
  const { galleryAndContentGroup, htmlIdRef, isSubContent } = layout;
  const [emblaRef, embla] = useEmblaCarousel({
    align: "start",
    active:
      galleryAndContentGroup.images?.nodes &&
      galleryAndContentGroup.images?.nodes?.length > 1,
  });
  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const hasButton =
    galleryAndContentGroup?.button && !galleryAndContentGroup?.button?.isHidden;

  const useVideo = !!(
    galleryAndContentGroup?.useVideo && galleryAndContentGroup?.contentVideo
  );

  // For videos, no option for fullHeight. Automatically true
  const isFullHeightImageClassname =
    galleryAndContentGroup?.isFullHeightImage || useVideo
      ? "relative"
      : "relative overflow-hidden h-60 md:h-80 lg:h-96";

  const isFullHeightImageProps = {
    ...(galleryAndContentGroup?.isFullHeightImage && {
      width: MAX_PIXEL_WIDTH,
      height: MAX_PIXEL_HEIGHT,
      color: "transparent",
    }),
    ...(!galleryAndContentGroup?.isFullHeightImage && {
      fill: true,
    }),
  };

  const { galleryAndContentHeading, galleryAndContentSubheading } =
    galleryAndContentGroup;

  const scrollTo = useCallback(
    (index: number) => embla && embla.scrollTo(index),
    [embla]
  );

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
  }, [embla, onSelect]);

  const renderButtonSection = () => {
    if (
      galleryAndContentGroup.button &&
      !galleryAndContentGroup.button.isHidden
    ) {
      return (
        <div className="mt-8 items-start justify-start text-left">
          {cmsButton(
            setButtonModalState,
            galleryAndContentGroup.button,
            htmlIdRef
          )}
        </div>
      );
    }

    return null;
  };

  const renderVideoSection = () => {
    return (
      <div className="w-full flex-1">
        <div className="relative">
          <div className="w-full overflow-hidden" ref={emblaRef}>
            <div className="flex select-none">
              <div className="relative min-w-full">
                <div className={isFullHeightImageClassname}>
                  <EmbeddedVideoPlayer
                    contentVideo={String(galleryAndContentGroup.contentVideo)}
                    roundEdges
                    fullWidth={
                      galleryAndContentGroup?.isFullWidthImage ?? false
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderImagesAndVideoSection = () => {
    if (useVideo) {
      return renderVideoSection();
    }
    if (galleryAndContentGroup.images && !useVideo) {
      return (
        <div className="w-full flex-1">
          <div className="relative">
            <div className="w-full overflow-hidden" ref={emblaRef}>
              <div className="flex select-none">
                {galleryAndContentGroup.images?.nodes?.map((image) => (
                  <div className="relative min-w-full" key={image?.sourceUrl}>
                    <div className={isFullHeightImageClassname}>
                      {cloudflareImage({
                        src: image.sourceUrl,
                        alt: image.altText,
                        ...isFullHeightImageProps,
                        className: classNames(
                          "h-auto w-full rounded-lg object-cover",
                          {
                            "cursor-pointer":
                              galleryAndContentGroup.images.nodes.length > 1,
                          }
                        ),
                      })}
                    </div>
                    {image.caption && (
                      <div
                        className="w-full pt-2 text-sm font-normal text-gray-700"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: image.caption,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              {galleryAndContentGroup.images?.nodes?.length > 1 && (
                <div className="absolute bottom-4 left-0 flex w-full justify-center gap-x-3">
                  {galleryAndContentGroup.images?.nodes?.map((image, index) => (
                    <DotButton
                      key={image?.sourceUrl}
                      selected={index === selectedIndex}
                      onClick={() => scrollTo(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      id="cms-gallery-and-content"
      className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
      style={GetBackgroundStyle(galleryAndContentGroup.background, baseDomain)}
    >
      <div>
        {galleryAndContentGroup.isFullWidthImage &&
          (galleryAndContentGroup.images || useVideo) && (
            <div
              className={classNames(
                "mx-auto flex max-w-page flex-col items-center gap-6",
                {
                  "px:0 py-2": isSubContent,
                  "px-4 pb-6 pt-10 lg:px-5 lg:py-16": !isSubContent,
                },
                {
                  "lg:gap-24": !hasButton,
                }
              )}
            >
              {renderImagesAndVideoSection()}
              {renderButtonSection()}
            </div>
          )}

        {!galleryAndContentGroup.isFullWidthImage && (
          <div
            className={classNames(
              "mx-auto flex max-w-page flex-col items-center gap-6 lg:gap-24",
              {
                "md:flex-row": galleryAndContentGroup.imagePosition === "Right",
                "md:flex-row-reverse":
                  galleryAndContentGroup.imagePosition === "Left",
                "px:0 py-2 md:flex-row": isSubContent,
                "px-4 pb-6 pt-10 lg:px-5 lg:py-16 md:flex-row": !isSubContent,
              }
            )}
          >
            <div className="flex-1">
              {galleryAndContentHeading && (
                <CmsHeading
                  headingLevel={
                    galleryAndContentHeading.headingGroup?.headingLevel
                  }
                  textSize={galleryAndContentHeading.headingGroup?.textSize}
                  text={galleryAndContentHeading.headingGroup?.text}
                  htmlId={galleryAndContentHeading.headingGroup?.htmlId}
                />
              )}
              {galleryAndContentSubheading && (
                <div className="mt-4">
                  <CmsHeading
                    headingLevel={
                      galleryAndContentSubheading.headingGroup?.headingLevel
                    }
                    textSize={
                      galleryAndContentSubheading.headingGroup?.textSize
                    }
                    text={galleryAndContentSubheading.headingGroup?.text}
                    htmlId={galleryAndContentSubheading.headingGroup?.htmlId}
                  />
                </div>
              )}
              {galleryAndContentGroup.contentType[0] === "text"
                ? galleryAndContentGroup.content && (
                    <div
                      className="mt-4 text-base font-normal lg:mt-8 lg:text-lg lg:leading-6 [&>ul]:list-inside [&>ul]:list-disc"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: galleryAndContentGroup.content,
                      }}
                    />
                  )
                : galleryAndContentGroup.contentRepeater?.length > 0 && (
                    <div className="mt-4 flex flex-col gap-8 text-base font-normal lg:mt-8 lg:text-lg lg:leading-6">
                      {galleryAndContentGroup.contentRepeater?.map(
                        (content) => {
                          const { altText, sourceUrl } =
                            (IsNodeStructure(content?.contentIcon)
                              ? content?.contentIcon.node
                              : content?.contentIcon) ?? {};

                          return (
                            <div
                              key={content.contentDescription}
                              className="flex gap-4 lg:leading-5"
                            >
                              {sourceUrl && (
                                <div
                                  className="relative h-16 w-16"
                                  key={`${altText}`}
                                >
                                  {cloudflareImage({
                                    src: sourceUrl,
                                    alt: altText,
                                    fill: true,
                                    className:
                                      "items-center justify-center text-center",
                                  })}
                                </div>
                              )}
                              {content.contentDescription && (
                                <div
                                  className="flex-1"
                                  // eslint-disable-next-line react/no-danger
                                  dangerouslySetInnerHTML={{
                                    __html: content.contentDescription,
                                  }}
                                />
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
              {renderButtonSection()}
            </div>
            {renderImagesAndVideoSection()}
          </div>
        )}
      </div>
      {buttonModalState.show && cmsButtonModalContainer(buttonModalState)}
    </div>
  );
};

export default {};
